import { EFinancingCompany, EFinancingProgram, EMerchantType } from '@if/core/types'
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded'
import { Button, Grid, MenuItem, SelectChangeEvent, TextField, Tooltip } from '@mui/material'
import { useSelectedCompany } from '@src/contexts/SelectedCompanyProvider'
import { debounce } from 'lodash-es'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AddBannerDialog from './AddBannerDialog'

interface Props {
  handleSearch: (value: string) => void
  financingProgramIds: EFinancingProgram[]
  setFinancingProgramIds: (ids: EFinancingProgram[]) => void
  nameInitValue: string
  availableProgramIds: EFinancingProgram[]
  selectedMerchantTypeId: EMerchantType
}

const BrowseMerchantToolbar = ({
  handleSearch,
  financingProgramIds,
  setFinancingProgramIds,
  nameInitValue,
  availableProgramIds,
  selectedMerchantTypeId,
}: Props) => {
  const { t } = useTranslation()
  const debounceOnChange = debounce(handleSearch, 500)
  const [openBannerDialog, setOpenBannerDialog] = useState(false)
  const selectedCompany = useSelectedCompany()

  const handleCloseProfile = () => {
    setOpenBannerDialog(false)
  }
  const handleAddBanner = () => {
    setOpenBannerDialog(true)
  }

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const {
      target: { value },
    } = event
    setFinancingProgramIds(value as EFinancingProgram[])
  }

  const getProgramName = useCallback(
    (program: EFinancingProgram) => {
      return t(`EFinancingProgram.${program}`, { ns: 'enum' })
    },
    [t],
  )

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid
          size={{
            sm: 12,
            xs: 6,
            md: 2,
          }}
        >
          <TextField
            label={t('common.search')}
            placeholder={t('common.name')}
            onChange={(e) => debounceOnChange(e.target.value)}
            defaultValue={nameInitValue}
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            sm: 12,
            xs: 6,
            md: 2,
          }}
        >
          <TextField
            label={t('common.program')}
            value={financingProgramIds}
            select
            slotProps={{ select: { multiple: true, onChange: handleChange } }}
            fullWidth
          >
            {availableProgramIds.map((program) => (
              <MenuItem key={program} value={program}>
                {getProgramName(program)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {selectedMerchantTypeId === EMerchantType.Banner && (
          <Grid
            size={{
              sm: 12,
              xs: 2,
              md: 1,
            }}
          >
            <Tooltip title={t('common.addBanner')}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddBanner}
                disabled={selectedCompany === EFinancingCompany.Iceberg}
                fullWidth
                style={{ height: '98%' }}
              >
                <AddCircleOutlineRoundedIcon />
              </Button>
            </Tooltip>
          </Grid>
        )}
      </Grid>
      <AddBannerDialog open={openBannerDialog} handleClose={handleCloseProfile} title={t('common.newBanner')} />
    </>
  )
}

export default BrowseMerchantToolbar
